import React, { FC, useEffect, useRef } from 'react';
import { Dialog, DialogContent, IconButton, Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTrackEvent } from '../../../common/hooks/useTrackEvent';
import { EventNames } from '../../../common/constants/constants';
import { CAV6 } from '../cav6tokens';

const DialogTop = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '14px 14px 10px 18px',
  borderBottom: '1px solid rgba(0,0,0,.08)',
});

const DialogTitle = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  fontFamily: CAV6.font,
  color: 'rgba(0,0,0,.72)',
});

const CloseButton = styled(IconButton)({
  width: '40px',
  height: '40px',
  borderRadius: CAV6.pillRadius,
  border: '1px solid rgba(0,0,0,.10)',
  background: '#fafafa',
});

const VideoWrap = styled(Box)({
  width: 'min(92vw, 420px)',
  maxWidth: '100%',
  aspectRatio: '9 / 16',
  height: 'auto',
  maxHeight: 'calc(100vh - 110px - env(safe-area-inset-top) - env(safe-area-inset-bottom))',
  borderRadius: '20px',
  overflow: 'hidden',
  background: '#000',
  margin: '0 auto',
  '@media (max-height: 640px)': {
    width: 'min(86vw, 360px)',
  },
  '& video': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
    background: '#000',
  },
});

interface Props {
  open: boolean;
  onClose: () => void;
  videoUrl: string;
}

export const WelcomeVideoModal: FC<Props> = ({ open, onClose, videoUrl }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const { trackEvent } = useTrackEvent();

  const openedAtRef = useRef<number | null>(null);
  const completedRef = useRef(false);
  const playedRef = useRef(false);
  const furthestRef = useRef(0);

  // Preserved tracking from WelcomeVideoPage, plus the v2 open/close pair.
  useEffect(() => {
    if (open) {
      openedAtRef.current = performance.now();
      completedRef.current = false;
      playedRef.current = false;
      furthestRef.current = 0;
      trackEvent({ action: EventNames.welcome_video_page_view });
      trackEvent({ action: EventNames.example_video_open });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
    trackEvent({
      action: EventNames.example_video_close,
      // The video has controls and no autoplay: `played` says whether the
      // person pressed play at all; watched_ms is the furthest point reached.
      played: playedRef.current,
      watched_ms: Math.round(furthestRef.current * 1000),
      duration_ms:
        videoRef.current && Number.isFinite(videoRef.current.duration)
          ? Math.round(videoRef.current.duration * 1000)
          : null,
      open_ms: openedAtRef.current ? Math.round(performance.now() - openedAtRef.current) : null,
      completed: completedRef.current,
      close_method: 'button_or_backdrop',
    });
    onClose();
  };

  const posterUrl = videoUrl?.replace(/\....$/, '.jpg').replace('/upload/', '/upload/so_0,');

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          borderRadius: '28px',
          maxHeight: 'calc(100vh - 24px - env(safe-area-inset-top) - env(safe-area-inset-bottom))',
          width: 'min(560px, 92vw)',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTop>
        <DialogTitle>Here&apos;s what we&apos;re looking for...</DialogTitle>
        <CloseButton onClick={handleClose} aria-label="Close">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="rgba(0,0,0,.70)" aria-hidden="true">
            <path d="M18.3 5.71a1 1 0 0 0-1.41 0L12 10.59 7.11 5.7A1 1 0 0 0 5.7 7.11L10.59 12 5.7 16.89a1 1 0 1 0 1.41 1.41L12 13.41l4.89 4.89a1 1 0 0 0 1.41-1.41L13.41 12l4.89-4.89a1 1 0 0 0 0-1.4z" />
          </svg>
        </CloseButton>
      </DialogTop>
      <DialogContent sx={{ padding: '14px', display: 'flex', justifyContent: 'center' }}>
        <VideoWrap>
          <video
            ref={videoRef}
            controls
            playsInline
            preload="metadata"
            poster={posterUrl}
            onPlay={() => {
              playedRef.current = true;
            }}
            onTimeUpdate={(e) => {
              furthestRef.current = Math.max(furthestRef.current, e.currentTarget.currentTime);
            }}
            onEnded={() => {
              completedRef.current = true;
              trackEvent({ action: EventNames.welcome_video_watched });
            }}
          >
            <source src={videoUrl} type="video/mp4" />
          </video>
        </VideoWrap>
      </DialogContent>
    </Dialog>
  );
};
